import { createSlice } from '@reduxjs/toolkit';
import { userNotification } from 'src/models/userNotification';
import { User, UserPoint, UserResponse, UserRoles } from 'src/models/user';

interface UserState {
  isLoading: boolean;
  isLoadingQuery: boolean;
  isErrorMessage: string;
  statusCode: number;
  users: User[];
  activeUser: User;
  userPaginated: User[];
  page: number;
  limit: number;
  total: number;
  changeUser: null;
  userPoints?: UserPoint[];
  changeUserPoint?: null;
  isLoadingUsersPoints: boolean;
  selectedUser: UserResponse | any;
  userFilters: any;
  userNotifiaction: userNotification;
  user: null;
  usersByTags: User[];
  loadingEdit: boolean;
  error: null;
  userRoles: UserRoles[];
  changeURoles: null;
  isLoadingUserRoles: boolean;
  userEditPage: User | null;
  isLoadingUserEditPage: boolean
  usersSearch: User[];
  rrppUsers: User[];
  isLoadingRrppUsers: boolean;
}

const initialState: UserState = {
  isLoading: false,
  isLoadingQuery: false,
  statusCode: 201,
  isErrorMessage: '',
  users: [],
  activeUser: null,
  userPaginated: [],
  page: 0,
  limit: 25,
  total: 0,
  changeUser: null,
  userPoints: [],
  changeUserPoint: null,
  isLoadingUsersPoints: false,
  selectedUser: {},
  userFilters: {
    filter: {
      search: null,
      birthdate: null,
      tags: null,
      roles: null,
      onlyDeleted: false
    },
    order: {
      desc: false,
      field: 'init_date'
    },
    isVerify: true
  },
  userNotifiaction: null,
  user: null,
  usersByTags: [],
  loadingEdit: false,
  error: null,
  userRoles: [],
  changeURoles: null,
  isLoadingUserRoles: false,
  userEditPage: null,
  isLoadingUserEditPage: false,
  usersSearch: [],
  rrppUsers: [],
  isLoadingRrppUsers: false
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoadingActive(state, action) {
      state.isLoading = action.payload;
    },
    startLoadingUsers: (state) => {
      state.isLoading = true;
    },
    setUsers: (state, action) => {
      state.isLoading = false;
      state.users = action.payload;
    },
    setUsersPaginated: (state, action) => {
      state.isLoading = false;
      state.page = action.payload.page;
      state.total = action.payload.total;
      state.userPaginated = action.payload.users;
    },
    setChangeUser: (state, action) => {
      state.changeUser = action.payload;
    },
    setError: (state, action) => {
      const message = action.payload.message;
      state.statusCode = action.payload.statusCode;
      state.isErrorMessage = message;
    },
    setUserPoints: (state, action) => {
      state.isLoadingUsersPoints = false;
      state.userPoints = action.payload;
    },
    clearUserPoints: (state, action) => {
      state.userPoints = action.payload;
    },
    startLoadingUsersPoints: (state) => {
      state.isLoadingUsersPoints = true;
    },
    setPoints: (state, action) => {
      state.changeUserPoint = action.payload;
      state.isLoading = false;
    },
    setErrorPoints: (state, action) => {
      const message = action.payload.message;
      state.statusCode = action.payload.status;
      state.isErrorMessage = message;
    },
    stopLoadingUsers: (state) => {
      state.isLoading = false;
    },
    setSelectedUser(state, action) {
      state.selectedUser = action.payload.data;
      state.isLoading = false;
      state.isErrorMessage = null;
    },
    resetSelectedUserState(state) {
      state.selectedUser = {};
    },
    stopLoadingUserPoints: (state) => {
      state.isLoadingUsersPoints = false;
    },
    setUserFilters: (state, action) => {
      state.userFilters = action.payload;
    },
    setNotification: (state, action) => {
      state.userNotifiaction = action.payload;
      state.isLoading = false;
    },
    getUserByIdRequest: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    getUserByIdSuccess: (state, action) => {
      // state.user = action.payload;
      state.selectedUser = action.payload;
      state.isLoading = false;
    },
    getUserByIdError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setActiveUser: (state, action) => {
      state.activeUser = action.payload;
    },
    startLoadingEdit: (state) => {
      state.loadingEdit = true;
    },
    stopLoadingEdit: (state) => {
      state.loadingEdit = false;
    },
    setOnlyUsers: (state, action) => {
      state.users = action.payload;
    },
    setUsersByTags: (state, action) => {
      state.usersByTags = action.payload;
    },
    isLoadingUserRoles: (state) => {
      state.isLoadingUserRoles = true;
    },
    setUserRoles: (state, action) => {
      state.isLoading = false;
      state.users = action.payload;
    },
    setChangeURoles: (state, action) => {
      state.changeUser = action.payload;
    },
    stopLoadingUserRoles: (state) => {
      state.isLoadingUserRoles = false;
    },
    clearUsers: (state) => {
      state.users = [];
      state.userPaginated = [];
      state.page = 0;
      state.total = 0;
    },
    setUserEditPage: (state, action) => {
      state.userEditPage = action.payload;
    },
    clearUserEditPage: (state) => {
      state.userEditPage = null;
    },
    startLoadingUserEditPage: (state) => {
      state.isLoadingUserEditPage = true;
    },
    stopLoadingUserEditPage: (state) => {
      state.isLoadingUserEditPage = false;
    },
    setUsersSearch: (state, action) => {
      state.usersSearch = action.payload;
    },
    setRrppUsers: (state, action) => {
      state.rrppUsers = action.payload;
    },
    startLoadingRrppUsers: (state) => {
      state.isLoadingRrppUsers = true;
    },
    stopLoadingRrppUsers: (state) => {
      state.isLoadingRrppUsers = false;
    },
    startLoadingQuery: (state) => {
      state.isLoadingQuery = true;
    },
    stopLoadingQuery: (state) => {
      state.isLoadingQuery = false;
    }
  }
});

export const {
  startLoadingUsers,
  setUsers,
  setUsersPaginated,
  setError,
  setChangeUser,
  setUserPoints,
  startLoadingUsersPoints,
  setPoints,
  setErrorPoints,
  stopLoadingUsers,
  setSelectedUser,
  stopLoadingUserPoints,
  setUserFilters,
  setNotification,
  resetSelectedUserState,
  getUserByIdRequest,
  getUserByIdSuccess,
  getUserByIdError,
  setActiveUser,
  clearUserPoints,
  startLoadingEdit,
  stopLoadingEdit,
  setLoadingActive,
  setOnlyUsers,
  setUsersByTags,
  isLoadingUserRoles,
  setUserRoles,
  setChangeURoles,
  stopLoadingUserRoles,
  clearUsers,
  setUserEditPage,
  clearUserEditPage,
  startLoadingUserEditPage,
  stopLoadingUserEditPage,
  setUsersSearch,
  startLoadingRrppUsers,
  stopLoadingRrppUsers,
  setRrppUsers,
  startLoadingQuery,
  stopLoadingQuery
} = userSlice.actions;
